import {
  MyThreadsListProvider,
  useApp,
  useAuth,
  useMessagingWidget,
  useMyMessageSound,
} from '@hb/react'
import { useMySettings } from '@hb/react/src/hooks/user/useMySettings'
import { documentId } from 'firebase/firestore'
import { PropsWithChildren, useMemo } from 'react'
import { useLocation } from 'react-router'
import useSound from 'use-sound'
import { searchMyThreads } from '../backend/functions/other'
import { View } from '../types/views'
import { useIsMainWindow } from './MainWindowContext'

export const WebThreadsListProvider = ({
  children,
  views,
}: PropsWithChildren<{ views: View[] }>) => {
  const isMainWindow = useIsMainWindow()
  const authUser = useAuth(s => s.authUser)
  const settings = useMySettings(authUser?.uid ?? null)
  const messageSound = useMyMessageSound(settings, 'messaging')
  const uid = useMemo(() => authUser?.uid ?? null, [authUser])
  const widget = useMessagingWidget(uid, searchMyThreads)
  const { data: widgetState } = widget ?? {}
  const { pathname } = useLocation()
  const { appName } = useApp()
  const activeView = useMemo(
    () =>
      views.find(v =>
        Array.isArray(v.path) ? v.path.find(p => pathname.startsWith(p)) : v.path === pathname,
      ),
    [views, pathname],
  )
  const forcePatientMessagesView = useMemo(() => {
    if (appName !== 'app') return false
    if (!activeView?.access) return true
    return activeView.access.includes('user')
  }, [activeView, appName])

  const soundOpts = useMemo(
    () => ({ volume: widgetState?.notificationVolume ?? 0.25 }),
    [widgetState],
  )
  const [play] = useSound(messageSound, soundOpts)
  const playNotificationSound = useMemo(() => {
    if (!isMainWindow) return null
    return play
  }, [isMainWindow, play])
  return (
    <MyThreadsListProvider
      getDocumentIdPath={documentId}
      forcePatientThreadsView={forcePatientMessagesView}
      playNotificationSound={playNotificationSound}
      widget={widget}
      uid={uid}>
      {children}
    </MyThreadsListProvider>
  )
}
